import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { TYPE_LABELS, TIME_SLOT_LABELS } from "@/data/content";

const ShootingCountdown = ({ date }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const target = new Date(date + "T09:00:00").getTime();
  const diff = target - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);

  return (
    <div className="mt-4 flex items-center gap-3 border border-gold/40 bg-gold/5 px-4 py-3" data-testid="shooting-countdown">
      <Sparkles size={14} className="shrink-0 text-gold" />
      <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white">
        Noch <span className="font-serif text-base normal-case tracking-normal text-gold">{days}</span> {days === 1 ? "Tag" : "Tage"}
        {days === 0 && <> und <span className="font-serif text-base normal-case tracking-normal text-gold">{hours}</span> Std.</>}
        {" "}bis zu deinem Shooting
      </p>
    </div>
  );
};

const STATUS = {
  neu: {
    label: "Anfrage eingegangen",
    cls: "text-gold border-gold/40",
    hint: "Eric prüft deinen Termin gerade — du bekommst eine E-Mail, sobald er bestätigt ist.",
  },
  bestaetigt: {
    label: "Bestätigt",
    cls: "text-emerald-400 border-emerald-400/40",
    hint: "Dein Termin steht fest! Eric meldet sich vorab noch einmal mit allen Details bei dir.",
  },
  abgelehnt: {
    label: "Leider nicht möglich",
    cls: "text-terracotta border-terracotta/40",
    hint: "Dieser Termin ist leider schon vergeben — frag einfach einen Alternativtermin an.",
  },
};

export const TerminStatus = () => {
  const [email, setEmail] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.get("/bookings/lookup", { params: { email } });
      setResults(res.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const fmt = (d) =>
    new Date(d + "T12:00:00").toLocaleDateString("de-DE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  return (
    <section id="mein-termin" data-testid="termin-status-section" className="border-t border-white/10">
      <div className="mx-auto max-w-[1440px] px-6 py-24 lg:px-12 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mx-auto max-w-2xl"
        >
          <p className="mb-4 text-center font-mono text-[11px] uppercase tracking-[0.35em] text-gold">Mein Termin — Status abrufen</p>
          <h2 className="text-center font-serif text-3xl font-light tracking-tight text-white sm:text-4xl">
            Schon angefragt? <span className="italic text-zinc-400">Hier siehst du deinen Termin.</span>
          </h2>
          <p className="mx-auto mt-5 max-w-md text-center text-sm leading-relaxed text-zinc-400">
            Gib die E-Mail-Adresse ein, mit der du deinen Termin angefragt hast — du siehst sofort,
            ob er bestätigt ist.
          </p>

          <form onSubmit={lookup} className="mt-10 flex flex-col gap-4 sm:flex-row" data-testid="termin-status-form">
            <input
              data-testid="termin-status-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="deine@email.de"
              className="input-line flex-1"
            />
            <button
              data-testid="termin-status-submit-button"
              type="submit"
              disabled={loading}
              className="flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-3.5 font-mono text-[10px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-white disabled:opacity-50"
            >
              <Search size={13} />
              {loading ? "Suche …" : "Status abrufen"}
            </button>
          </form>

          {results !== null && (
            <div className="mt-10 space-y-4">
              {results.length === 0 ? (
                <p className="border border-white/10 bg-ink2 p-8 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500" data-testid="termin-status-empty">
                  Keine Anfrage zu dieser E-Mail gefunden
                </p>
              ) : (
                results.map((b) => {
                  const s = STATUS[b.status] || STATUS.neu;
                  return (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="border border-white/10 bg-ink2 p-6"
                      data-testid={`termin-status-card-${b.id}`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <CalendarCheck size={18} className="text-gold" />
                          <p className="font-serif text-xl text-white">{fmt(b.date)}</p>
                        </div>
                        <span className={`rounded-full border px-3 py-1 font-mono text-[9px] uppercase tracking-[0.2em] ${s.cls}`}>
                          {s.label}
                        </span>
                      </div>
                      <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                        {TYPE_LABELS[b.shoot_type] || b.shoot_type}
                        {b.time_slot ? ` · ${TIME_SLOT_LABELS[b.time_slot] || b.time_slot}` : ""}
                      </p>
                      <p className="mt-3 text-sm text-zinc-400">{s.hint}</p>
                      {b.status === "bestaetigt" && <ShootingCountdown date={b.date} />}
                    </motion.div>
                  );
                })
              )}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
